<template>
  <form @submit.prevent="submit" class="agent-form">

    <!-- Nombre -->
    <div class="field">
      <label>Nombre del agente <span class="required">*</span></label>
      <InputText
        v-model="form.name"
        placeholder="Ej: Analista Financiero"
        class="w-full"
        :class="{ 'p-invalid': errors.name }"
        required
      />
      <small class="p-error" v-if="errors.name">{{ errors.name }}</small>
    </div>

    <!-- Categoría -->
    <div class="field">
      <label>Categoría <span class="required">*</span></label>
      <Dropdown
        v-model="form.category"
        :options="categories"
        optionLabel="label"
        optionValue="value"
        placeholder="Selecciona una categoría"
        class="w-full"
        :class="{ 'p-invalid': errors.category }"
      />
      <small class="p-error" v-if="errors.category">{{ errors.category }}</small>
    </div>

    <!-- Descripción -->
    <div class="field">
      <label>Descripción</label>
      <Textarea
        v-model="form.description"
        placeholder="Describe qué hace este agente..."
        rows="2"
        class="w-full"
        autoResize
      />
    </div>

    <!-- Prompt del sistema -->
    <div class="field">
      <div class="prompt-label-row">
        <label>
          Prompt del sistema <span class="required">*</span>
        </label>
        <div class="prompt-actions">
          <Button
            :icon="promptExpanded ? 'pi pi-compress' : 'pi pi-expand'"
            :label="promptExpanded ? 'Contraer' : 'Expandir'"
            text
            size="small"
            @click="promptExpanded = !promptExpanded"
          />
          <Button
            icon="pi pi-eye"
            label="Vista previa"
            text
            size="small"
            @click="showPreview = !showPreview"
          />
        </div>
      </div>

      <!-- Variables disponibles -->
      <div class="variables-bar">
        <span class="variables-label">Variables:</span>
        <button
          v-for="v in variables"
          :key="v.key"
          type="button"
          class="var-chip"
          v-tooltip="v.description"
          @click="insertVariable(v.key)"
        >
          {{ v.key }}
        </button>
      </div>

      <!-- Editor del prompt -->
      <Textarea
        ref="promptRef"
        v-model="form.prompt"
        :placeholder="promptPlaceholder"
        :rows="promptExpanded ? 20 : 8"
        class="w-full prompt-editor"
        :class="{ 'p-invalid': errors.prompt, expanded: promptExpanded }"
        autoResize
      />

      <div class="prompt-footer">
        <small class="p-error" v-if="errors.prompt">{{ errors.prompt }}</small>
        <span class="char-count" :class="{ warn: form.prompt.length > 3000 }">
          {{ form.prompt.length.toLocaleString() }} caracteres
        </span>
      </div>

      <!-- Vista previa del prompt renderizado -->
      <div v-if="showPreview && form.prompt" class="prompt-preview">
        <div class="preview-header">
          <i class="pi pi-eye" />
          Vista previa — variables reemplazadas con valores de ejemplo
        </div>
        <pre class="preview-content">{{ renderedPrompt }}</pre>
      </div>
    </div>

    <!-- Proveedor de IA -->
    <div class="field-row">
      <div class="field">
        <label>Proveedor de IA</label>
        <Dropdown
          v-model="form.ai_provider_id"
          :options="providers"
          optionLabel="name"
          optionValue="id"
          placeholder="Por defecto del sistema"
          class="w-full"
          showClear
          @change="onProviderChange"
        />
      </div>
      <div class="field">
        <label>Modelo</label>
        <Dropdown
          v-model="form.ai_model_id"
          :options="filteredModels"
          optionLabel="model_name"
          optionValue="id"
          placeholder="Por defecto del proveedor"
          class="w-full"
          showClear
          :disabled="!form.ai_provider_id"
        />
      </div>
    </div>

    <!-- Estado (solo en edición) -->
    <div class="field" v-if="isEditing">
      <div class="toggle-row">
        <label>Agente activo</label>
        <ToggleButton
          v-model="form.is_active"
          onLabel="Activo"
          offLabel="Inactivo"
          onIcon="pi pi-check"
          offIcon="pi pi-times"
        />
      </div>
    </div>

    <!-- Acciones -->
    <div class="form-actions">
      <Button
        label="Cancelar"
        severity="secondary"
        type="button"
        icon="pi pi-times"
        @click="$emit('cancel')"
      />
      <Button
        :label="isEditing ? 'Guardar cambios' : 'Crear agente'"
        :icon="isEditing ? 'pi pi-check' : 'pi pi-plus'"
        type="submit"
        :loading="loading"
      />
    </div>
  </form>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import ToggleButton from 'primevue/togglebutton';
import { agentsService } from '../../services/agents';
import api from '../../services/api';

const props = defineProps({
  agent: { type: Object, default: null },
});
const emit = defineEmits(['saved', 'cancel']);

const isEditing = computed(() => !!props.agent);
const loading = ref(false);
const providers = ref([]);
const allModels = ref([]);
const errors = ref({});
const promptExpanded = ref(false);
const showPreview = ref(false);
const promptRef = ref(null);

// Variables disponibles en el prompt
const variables = [
  { key: '{{tenant_name}}',    description: 'Nombre del tenant/empresa que usa el agente' },
  { key: '{{user_name}}',      description: 'Nombre del usuario que está chateando' },
  { key: '{{current_date}}',   description: 'Fecha actual (YYYY-MM-DD)' },
  { key: '{{agent_name}}',     description: 'Nombre de este agente' },
];

const promptPlaceholder = `Eres un asistente especializado para {{tenant_name}}.

Tu objetivo es [describe el objetivo principal].

Instrucciones:
- Responde siempre en español
- Sé conciso y profesional
- Si no sabes algo, indícalo claramente

Restricciones:
- No compartas información confidencial
- No realices acciones fuera de tu ámbito`;

// Vista previa con variables reemplazadas
const renderedPrompt = computed(() =>
  form.value.prompt
    .replace(/\{\{tenant_name\}\}/g, 'Empresa Demo S.A.S')
    .replace(/\{\{user_name\}\}/g, 'Juan Pérez')
    .replace(/\{\{current_date\}\}/g, new Date().toISOString().split('T')[0])
    .replace(/\{\{agent_name\}\}/g, form.value.name || 'Agente')
);

// Insertar variable en la posición del cursor
const insertVariable = (variable) => {
  const textarea = promptRef.value?.$el;
  if (!textarea) {
    form.value.prompt += variable;
    return;
  }
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const before = form.value.prompt.substring(0, start);
  const after = form.value.prompt.substring(end);
  form.value.prompt = before + variable + after;
  // Restaurar cursor después de la variable insertada
  setTimeout(() => {
    textarea.selectionStart = textarea.selectionEnd = start + variable.length;
    textarea.focus();
  }, 0);
};

const categories = [
  { label: '🤖 General', value: 'general' },
  { label: '📊 Financiero / Contable', value: 'financiero' },
  { label: '🛠️ Soporte Técnico', value: 'soporte' },
  { label: '💼 Ventas', value: 'ventas' },
  { label: '⚖️ Legal', value: 'legal' },
  { label: '👥 Recursos Humanos', value: 'rrhh' },
  { label: '📦 Logística', value: 'logistica' },
  { label: '🏥 Salud', value: 'salud' },
  { label: '🎓 Educación', value: 'educacion' },
];

const form = ref({
  name: '',
  category: 'general',
  description: '',
  prompt: '',
  ai_provider_id: null,
  ai_model_id: null,
  is_active: true,
});

// Inicializar con datos del agente si es edición
watch(() => props.agent, (agent) => {
  if (agent) {
    form.value = {
      name: agent.name || '',
      category: agent.category || 'general',
      description: agent.description || '',
      prompt: agent.prompt || '',
      ai_provider_id: agent.ai_provider_id || null,
      ai_model_id: agent.ai_model_id || null,
      is_active: agent.is_active !== false,
    };
  }
}, { immediate: true });

const filteredModels = computed(() => {
  if (!form.value.ai_provider_id) return [];
  return allModels.value.filter(m => m.ai_provider_id === form.value.ai_provider_id);
});

const onProviderChange = () => {
  form.value.ai_model_id = null;
};

const validate = () => {
  errors.value = {};
  if (!form.value.name.trim()) errors.value.name = 'El nombre es obligatorio';
  if (!form.value.category) errors.value.category = 'La categoría es obligatoria';
  if (!form.value.prompt.trim()) errors.value.prompt = 'El prompt del sistema es obligatorio';
  if (form.value.prompt.trim().length < 20) errors.value.prompt = 'El prompt debe tener al menos 20 caracteres';
  return Object.keys(errors.value).length === 0;
};

const submit = async () => {
  if (!validate()) return;
  loading.value = true;
  try {
    const payload = {
      name: form.value.name.trim(),
      category: form.value.category,
      description: form.value.description.trim() || null,
      prompt: form.value.prompt.trim(),
      ai_provider_id: form.value.ai_provider_id || null,
      ai_model_id: form.value.ai_model_id || null,
    };
    if (isEditing.value) {
      payload.is_active = form.value.is_active;
      await agentsService.updateAgent(props.agent.id, payload);
    } else {
      await agentsService.createAgent(payload);
    }
    emit('saved');
  } catch (e) {
    const detail = e.response?.data?.detail || e.message;
    errors.value.general = detail;
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  try {
    // Cargar proveedores y modelos disponibles desde el backend
    const [provRes, modRes] = await Promise.all([
      api.get('/ai-providers/'),
      api.get('/ai-providers/models'),
    ]);
    
    providers.value = provRes.data || [];
    allModels.value = modRes.data || [];
    
    // Si no hay datos, usar fallback
    if (providers.value.length === 0) {
      providers.value = [
        { id: 1, name: 'Mistral AI' },
        { id: 2, name: 'OpenAI' },
      ];
    }
    if (allModels.value.length === 0) {
      allModels.value = [
        { id: 1, ai_provider_id: 1, model_name: 'mistral-small-latest' },
        { id: 2, ai_provider_id: 1, model_name: 'mistral-medium-latest' },
        { id: 3, ai_provider_id: 1, model_name: 'mistral-large-latest' },
        { id: 4, ai_provider_id: 2, model_name: 'gpt-3.5-turbo' },
        { id: 5, ai_provider_id: 2, model_name: 'gpt-4-turbo' },
      ];
    }
  } catch (e) {
    console.error('Error cargando proveedores y modelos:', e);
    // Usar fallback en caso de error
    providers.value = [
      { id: 1, name: 'Mistral AI' },
      { id: 2, name: 'OpenAI' },
    ];
    allModels.value = [
      { id: 1, ai_provider_id: 1, model_name: 'mistral-small-latest' },
      { id: 2, ai_provider_id: 1, model_name: 'mistral-medium-latest' },
      { id: 3, ai_provider_id: 1, model_name: 'mistral-large-latest' },
      { id: 4, ai_provider_id: 2, model_name: 'gpt-3.5-turbo' },
      { id: 5, ai_provider_id: 2, model_name: 'gpt-4-turbo' },
    ];
  }
});
</script>

<style scoped>
.agent-form { display: flex; flex-direction: column; gap: 1.25rem; }

.field { display: flex; flex-direction: column; gap: 0.35rem; }
.field label { font-weight: 600; font-size: 0.875rem; color: #374151; }
.required { color: #ef4444; }
.hint { font-weight: 400; font-size: 0.75rem; color: #6b7280; margin-left: 0.5rem; }
.field-hint { font-size: 0.75rem; color: #9ca3af; }

.field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }

.toggle-row { display: flex; align-items: center; justify-content: space-between; }

/* Prompt editor */
.prompt-label-row {
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.5rem;
}
.prompt-actions { display: flex; gap: 0.25rem; }

.variables-bar {
  display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap;
  padding: 0.5rem 0.75rem; background: #f8fafc; border-radius: 6px;
  border: 1px solid #e2e8f0; font-size: 0.8rem;
}
.variables-label { color: #6b7280; font-weight: 600; flex-shrink: 0; }
.var-chip {
  background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe;
  border-radius: 4px; padding: 0.15rem 0.5rem; font-size: 0.75rem;
  font-family: monospace; cursor: pointer; transition: all 0.15s;
}
.var-chip:hover { background: #dbeafe; }

.prompt-editor {
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  resize: vertical;
}
.prompt-editor.expanded { min-height: 400px; }

.prompt-footer {
  display: flex; justify-content: space-between; align-items: center;
}
.char-count { font-size: 0.75rem; color: #9ca3af; }
.char-count.warn { color: #f59e0b; font-weight: 600; }

.prompt-preview {
  border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;
  margin-top: 0.25rem;
}
.preview-header {
  display: flex; align-items: center; gap: 0.5rem;
  padding: 0.5rem 0.875rem; background: #f1f5f9;
  font-size: 0.8rem; font-weight: 600; color: #475569;
  border-bottom: 1px solid #e2e8f0;
}
.preview-content {
  padding: 0.875rem; margin: 0; font-size: 0.82rem; line-height: 1.6;
  color: #374151; white-space: pre-wrap; word-break: break-word;
  background: #fafafa; max-height: 300px; overflow-y: auto;
  font-family: inherit;
}

.form-actions {
  display: flex; justify-content: flex-end; gap: 0.75rem;
  padding-top: 0.75rem; border-top: 1px solid #f3f4f6;
}

.w-full { width: 100%; }
</style>
